import { useEffect, useState } from 'react'

import mentorService from 'src/services/mentor.service'

/**
 * Encapsula la configuración de versión (v1/v2) de módulos por empresa
 * (ATL-743). Carga la lista al abrir, mantiene el estado editable en memoria y
 * al guardar envía únicamente los módulos que cambiaron respecto al snapshot
 * original (el backend hace upsert parcial: los no enviados quedan intactos).
 *
 * No usa redux: el modal es autocontenido. Los errores se manejan en el
 * call-site vía el resultado `{ ok }` que resuelven `load` y `save`.
 */
export const useModuleVersionConfig = (companyId: number | null, show: boolean) => {
  const [modules, setModules] = useState<ModuleVersion[]>([])
  const [original, setOriginal] = useState<ModuleVersion[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const apply = (list: ModuleVersion[]) => {
    setModules(list)
    setOriginal(list)
  }

  const load = async () => {
    if (companyId == null) return { ok: false }
    setLoading(true)
    try {
      apply(await mentorService.getModuleVersions(companyId))
      return { ok: true }
    } catch (error) {
      return { ok: false, error }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show && companyId != null) load()
    else {
      setModules([])
      setOriginal([])
    }
  }, [show, companyId])

  const toggle = (moduleId: number) =>
    setModules(prev => prev.map(m => (m.moduleId === moduleId ? { ...m, v2: !m.v2 } : m)))

  const setAll = (v2: boolean) => setModules(prev => prev.map(m => ({ ...m, v2 })))

  const changes = modules.filter(m => {
    const prev = original.find(o => o.moduleId === m.moduleId)
    return prev !== undefined && prev.v2 !== m.v2
  })

  const save = async () => {
    if (companyId == null) return { ok: false }
    if (changes.length === 0) return { ok: true }
    setSaving(true)
    try {
      const request: ModuleVersionRequest[] = changes.map(m => ({
        moduleId: m.moduleId,
        v2: m.v2,
      }))
      apply(await mentorService.saveModuleVersions(companyId, request))
      return { ok: true }
    } catch (error) {
      return { ok: false, error }
    } finally {
      setSaving(false)
    }
  }

  return {
    modules,
    loading,
    saving,
    toggle,
    setAll,
    save,
    hasChanges: changes.length > 0,
  }
}
